import healthCourseAlternativeImage from 'Assets/Images/health-course-alternative.webp'
import { FiChevronRight } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const Wrapper = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Separator = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  margin-top: 18px;
`

const SeparatorLine = styled.span`
  background: ${({ theme }) => theme.colors.grey300};
  height: 2px;
  width: 12px;
`

const SeparatorText = styled.span`
  color: ${({ theme }) => theme.colors.grey500};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0 12px;
`

const Card = styled.a`
  align-items: center;
  background: ${({ theme }) => theme.colors.green50};
  border: 1px solid ${({ theme }) => theme.colors.green200};
  border-radius: 8px;
  box-sizing: border-box;
  color: inherit;
  display: flex;
  max-width: 400px;
  min-height: 112px;
  overflow: hidden;
  text-decoration: none;
  transition: background-color 150ms ease, border-color 150ms ease;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.green100};
    border-color: ${({ theme }) => theme.colors.green300};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.green500};
    outline-offset: 2px;
  }
`

const Image = styled.img`
  flex: 0 0 108px;
  height: 104px;
  margin-left: 4px;
  object-fit: contain;
  width: 108px;

  @media(max-width: 360px) {
    flex-basis: 92px;
    height: 96px;
    width: 92px;
  }
`

const Copy = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-left: 8px;
  min-width: 0;
  padding: 12px 0;
`

const Title = styled.span`
  color: ${({ theme }) => theme.colors.grey950};
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
`

const Subtitle = styled.span`
  color: ${({ theme }) => theme.colors.grey500};
  font-size: 14px;
  line-height: 20px;
  margin-top: 4px;
`

const Chevron = styled(FiChevronRight)`
  color: ${({ theme }) => theme.colors.green700};
  flex: 0 0 28px;
  height: 28px;
  margin: 0 4px;
  width: 28px;
`

interface Props {
  href: string
  onClick: () => void
}

const HealthCourseAlternative = ({ href, onClick }: Props): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Wrapper>
      <Separator aria-hidden='true'>
        <SeparatorLine />
        <SeparatorText>{t('healthCourseAlternative.separator')}</SeparatorText>
        <SeparatorLine />
      </Separator>
      <Card
        href={href}
        onClick={onClick}
        rel='noopener noreferrer'
        target='_blank'
        data-testid='health-course-alternative-link'
      >
        <Image src={healthCourseAlternativeImage} alt='' />
        <Copy>
          <Title>{t('healthCourseAlternative.title')}</Title>
          <Subtitle>{t('healthCourseAlternative.subtitle')}</Subtitle>
        </Copy>
        <Chevron aria-hidden='true' />
      </Card>
    </Wrapper>
  )
}

export default HealthCourseAlternative
